import React, { useState } from 'react';
import { statusBadgeText } from '../../utils/BadgeEssay';
import moment from 'moment';
import { FiEdit3, FiEye, FiUpload } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { ModalWarning } from '@/components/custom/modal/modal-warning';
import { useClient } from 'jsonapi-react';
import usePermission from '@/hooks/usePermission';
import { PlayfulCardButton } from '@/components/custom/playful-card';
import { Card, CardBody, CardBodyContent, CardBodyHeader, CardBodyHeaderContent, CardContent, CardFooter, CardKpi, CardKpiGrid, CardKpiTitle, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

// Mesma paleta soft usada nas demais telas de redação do professor (Playful) —
// duplicado de propósito, cada tela mantém seu próprio mapeamento visual.
const STATUS_BADGE_VARIANT = {
  accepting_proposal: 'info-soft',
  processing: 'amber-soft',
  reviewing: 'amber-soft',
  ocr_review: 'amber-soft',
  sending_by_teacher: 'amber-soft',
  unreviewed: 'success-soft',
  reviewed: 'success-soft',
  annulled: 'danger-soft',
  annulled_by_teacher: 'danger-soft',
  minimum_length_not_satisfied: 'danger-soft',
  failed_to_process: 'danger-soft',
  close_for_submissions: 'muted',
  not_sent: 'muted'
};

export function EssayMonitoringPlayfulCardMobile({ student, essay, essayId, currentUser }) {
  const navigate = useNavigate();
  const badgeVariant = STATUS_BADGE_VARIANT?.[student.status];
  const badgeText = statusBadgeText?.[student.status];
  const [openWarning, setOpenWarning] = useState(false);
  const client = useClient();

  const canSend = usePermission('Enviar redação', 'Redação');

  const handleReviewEssay = () => {
    if (student.status === 'unreviewed') {
      client.mutate(['essays', essayId, 'essay_students', student.id], { status: 'reviewing' });
    }
    navigate(`/redacao/correcao/${student.id}/${essayId}`);
    setOpenWarning(false);
  };

  function renderAction() {
    const { status, id, corrector, user } = student;

    const viewEnabled =
      status === 'reviewed' || // corrigida pelo professor
      status === 'unreviewed' || // correção por IA
      status === 'annulled_by_teacher' || // anulada pelo professor
      status === 'reviewing' || // está sendo corrigida
      status === 'annulled'; // anulada

    const reviewEnabled =
      status === 'unreviewed' || // correção por IA
      (status === 'reviewing' && corrector.id === currentUser); // corrigindo e quem está corrigindo é o usuário atual

    const reachedSubmissionLimit = () => {
      const limit = essay['allow-multiple-submissions'] ? 2 : 1;
      return student['submission_order'] >= limit;
    };

    const isInvalid = () => ['annulled', 'annulled_by_teacher', 'minimum_length_not_satisfied'].includes(student.status);

    const sendDisabled =
      !canSend || // não tem permissão
      (reachedSubmissionLimit() && status !== 'ocr_review') || // já enviou a quantidade permitida
      status === 'reviewing' || // está sendo corrigida
      status === 'processing' || // está sendo processada
      (isInvalid() && reachedSubmissionLimit()) || // é inválida e já enviou a quantidade permitida
      status === 'close_for_submissions' || // prazo de envio encerrado
      essay.status === 'close_for_submissions' || // prazo de envio encerrado (status geral da redação)
      status === 'unreviewed'; // correção por IA

    return (
      <div className="flex w-full flex-col gap-2">
        <PlayfulCardButton
          variant="outline"
          icon={FiUpload}
          onClick={() => navigate(`/redacao/enviar/${essayId}/${user?.id}`)}
          disabled={sendDisabled}
          className="h-10"
        >
          Enviar redação
        </PlayfulCardButton>
        <PlayfulCardButton
          variant="outline"
          icon={FiEye}
          onClick={() => navigate(`/redacao/visualizar/${id}/${essayId}`)}
          disabled={!viewEnabled}
          className="h-10"
        >
          Visualizar
        </PlayfulCardButton>
        <PlayfulCardButton
          variant="outline"
          icon={FiEdit3}
          onClick={() => {
            if (status === 'reviewing' && corrector.id === currentUser) {
              navigate(`/redacao/correcao/${id}/${essayId}`);
            } else {
              setOpenWarning(true);
            }
          }}
          disabled={!reviewEnabled}
          className="h-10"
        >
          Corrigir
        </PlayfulCardButton>
      </div>
    );
  }

  return (
    <>
      <Card>
        <CardContent className="flex-col items-stretch">
          <CardBody className="w-full">
            <CardBodyHeader>
              <CardBodyHeaderContent>
                <CardTitle title={student?.user?.name}>{student?.user?.name}</CardTitle>
              </CardBodyHeaderContent>

              <Badge
                title={badgeText}
                variant={badgeVariant}
                className="shrink-0 rounded-full px-2.5 py-1 text-xs font-bold normal-case"
                size="xs"
              >
                {badgeText}
              </Badge>
            </CardBodyHeader>

            <CardBodyContent>
              <CardKpiGrid>
                <CardKpi>
                  <CardKpiTitle>Entrega</CardKpiTitle>
                  {student?.['delivery-date'] ? moment(student?.['delivery-date']).format('DD/MM/YYYY') : '-'}
                </CardKpi>
                <CardKpi>
                  <CardKpiTitle>Nota</CardKpiTitle>
                  {student?.grade ?? '-'}
                </CardKpi>
              </CardKpiGrid>

              <CardKpi>
                <CardKpiTitle>Corrigido por</CardKpiTitle>
                {student?.corrector?.name ?? '-'}
              </CardKpi>
            </CardBodyContent>
          </CardBody>

          <CardFooter>{renderAction()}</CardFooter>
        </CardContent>
      </Card>

      <ModalWarning
        show={openWarning}
        onClose={() => setOpenWarning(false)}
        titleFirstButton="Não"
        titleSecondButton="Sim"
        title={`Deseja mesmo corrigir essa redação?`}
        description="Outros professores não poderão corrigi-la."
        onSubmitFirstButton={() => setOpenWarning(false)}
        onSubmitSecondButton={() => handleReviewEssay()}
      />
    </>
  );
}
